import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { BRAND, GALLERY } from '@/data/content';
import { SectionHeading, PortfolioImage } from '@/components/site/SiteBits';
import { Seo, Breadcrumbs, BUSINESS_ID, SITE_URL } from '@/components/site/Seo';
import FinalCTA from '@/components/site/FinalCTA';

const OCCASIONS = [
  { icon: 'Sparkles', title: 'Christmas', text: 'Classic warm white, red and green, candy-cane patterns — built once, replayed every year from the app.' },
  { icon: 'Ghost', title: 'Halloween', text: 'Orange and purple scenes for October without a single trip up the ladder.' },
  { icon: 'Flag', title: 'Fourth of July', text: 'Red, white, and blue roofline looks for summer holidays and game days.' },
  { icon: 'Home', title: 'Everyday', text: 'Soft warm-white accent lighting the rest of the year — curb appeal every night, not just in December.' },
];

const STEPS = [
  { n: '01', title: 'Send your roofline', text: 'A photo or two of the front of your home and your address is all we need to start a written estimate.' },
  { n: '02', title: 'We install the ShawTech system', text: 'Low-profile LED track mounted straight along the roofline, wiring routed out of sight, built for Texas weather.' },
  { n: '03', title: 'You run it from your phone', text: 'We connect the app, build your first holiday scenes with you, and make sure you are comfortable before we leave.' },
];

const FAQ = [
  {
    q: 'How early should I book for the holidays?',
    a: 'As early as you can. Holiday-season install slots fill up across DFW, and every job starts with a written estimate. Sending your roofline photos early gives the best chance of being lit up well before the season.',
  },
  {
    q: 'Is this the same as hanging Christmas lights every year?',
    a: 'No. Permanent lighting is installed once along the roofline and stays up year-round. Colors, scenes, and schedules are controlled from an app — Christmas looks in December, warm white the rest of the year, and nothing to hang or take down again.',
  },
  {
    q: 'Is the lighting visible during the day?',
    a: 'The ShawTech system mounts in a low-profile track along the roofline that is designed to blend with the trim. Most homeowners find it barely noticeable by day.',
  },
  {
    q: 'What does holiday permanent lighting cost?',
    a: 'Every roofline is different, so pricing follows a look at your home. Send your address and a photo or two of the front roofline through the estimate form and the owner will follow up with a written scope — no obligation.',
  },
];

export default function SeasonalLighting() {
  const project = GALLERY.find((g) => g.id === 'permanent-lighting-roofline');
  const path = '/seasonal-lighting';
  const title = 'Holiday & Christmas Permanent Lighting in Fort Worth, TX | HearthOak';
  const description =
    'Permanent holiday lighting installed once and enjoyed every season — ShawTech LED roofline lighting with app-controlled colors, installed across Fort Worth & DFW by an Authorized ShawTech Installation Partner. Book early for the holidays.';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Holiday & Seasonal Permanent Lighting',
      name: 'Holiday & Seasonal Permanent Lighting',
      provider: { '@id': BUSINESS_ID },
      areaServed: ['Fort Worth, TX', 'Burleson, TX', 'Weatherford, TX', 'Granbury, TX', 'Cleburne, TX'],
      url: `${SITE_URL}${path}`,
      description,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  return (
    <div data-testid="page-seasonal-lighting">
      <Seo title={title} description={description} path={path} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="border-b border-brand-border bg-brand-bone">
        <div className="container-x py-14 lg:py-20">
          <Breadcrumbs
            items={[
              { label: 'Home', to: '/' },
              { label: 'Services', to: '/services' },
              { label: 'Permanent Lighting', to: '/services/permanent-lighting' },
              { label: 'Holiday & Seasonal' },
            ]}
            testId="breadcrumbs-seasonal-lighting"
          />
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
            <div>
              <Icons.Sparkles size={30} strokeWidth={1.5} className="text-brand-maroon mt-2" />
              <h1 className="h1 mt-4">Holiday lighting, installed once.</h1>
              <p className="body-lg mt-6 max-w-xl">
                This year, skip the ladder. ShawTech permanent LED lighting goes up once along your roofline and runs every holiday from an app — Christmas, Halloween, the Fourth of July, and warm white every night in between.
              </p>
              <p className="mt-4 font-sans text-sm text-brand-charcoal/80 max-w-xl border-l-2 border-brand-maroon pl-4" data-testid="seasonal-book-early-note">
                Holiday install slots fill up across DFW every season. Requesting your estimate early gives the best chance of being lit up well before the holidays.
              </p>
              <div className="mt-8 flex gap-3 flex-col sm:flex-row">
                <Link to="/estimate" className="btn-primary" data-testid="seasonal-cta-estimate">Request an Estimate</Link>
                <a href={BRAND.phoneHref} className="btn-secondary" data-testid="seasonal-cta-call">Call {BRAND.phoneDisplay}</a>
              </div>
            </div>
            <div>
              <div className="crop-frame w-full aspect-[16/10] border border-brand-border mb-6">
                <img
                  src="/portfolio/shawtech-christmas-redwhite.webp"
                  alt="ShawTech permanent LED lighting in red and white Christmas colors along a home's roofline"
                  className="w-full h-full object-cover"
                  loading="eager"
                  data-testid="seasonal-hero-img"
                />
              </div>
              <div className="border border-brand-border bg-brand-bone p-8">
                <img
                  src="/brand/shawtech-square-light.webp"
                  alt="Installation Partner of ShawTech Lighting"
                  className="w-40 h-auto mb-6"
                  loading="lazy"
                  data-testid="seasonal-shawtech-badge"
                />
                <div className="eyebrow mb-3">Installed by HearthOak</div>
                <p className="font-sans text-sm text-brand-charcoal leading-relaxed">
                  HearthOak Construction is an Authorized Installation Partner of ShawTech Lighting — the genuine system, mounted clean, with a full app walkthrough before we leave.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Occasions */}
      <section className="border-b border-brand-border">
        <div className="container-x py-16 lg:py-24">
          <SectionHeading eyebrow="One install, every occasion" title="Not just for Christmas" />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="seasonal-occasions">
            {OCCASIONS.map((o) => {
              const Icon = Icons[o.icon] || Icons.Sparkles;
              return (
                <div key={o.title} className="p-6 border border-brand-border bg-brand-bone">
                  <Icon size={22} strokeWidth={1.5} className="text-brand-maroon" />
                  <div className="mt-4 font-serif text-lg text-brand-charcoal">{o.title}</div>
                  <p className="mt-2 font-sans text-sm text-brand-muted leading-relaxed">{o.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="surface-paper border-b border-brand-border">
        <div className="container-x py-16 lg:py-24">
          <SectionHeading eyebrow="How it works" title="From estimate to first light-up" />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="seasonal-steps">
            {STEPS.map((s) => (
              <div key={s.n} className="p-6 border border-brand-border bg-brand-bone">
                <div className="font-serif text-3xl text-brand-maroon/70">{s.n}</div>
                <div className="mt-3 font-serif text-lg text-brand-charcoal">{s.title}</div>
                <p className="mt-2 font-sans text-sm text-brand-muted leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
            <div className="crop-frame border border-brand-border aspect-[16/10]">
              <img
                src="/portfolio/shawtech-rainbow-roofline.webp"
                alt="ShawTech permanent roofline lighting running a full-color scene at night"
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="seasonal-rainbow-img"
              />
            </div>
            {project && (
              <Link
                to={`/gallery/${project.id}`}
                className="group flex flex-col border border-brand-border bg-brand-bone hover:border-brand-charcoal transition-colors"
                data-testid="seasonal-project-link"
              >
                <PortfolioImage src={project.cover || project.src} className="aspect-[16/10]" alt={project.title} />
                <div className="p-5">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-brand-maroon">Our work</div>
                  <div className="mt-1 font-serif text-lg text-brand-charcoal group-hover:text-brand-maroon transition-colors">{project.title}</div>
                  <p className="mt-1 font-sans text-xs text-brand-muted leading-relaxed">{project.caption}</p>
                </div>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-brand-border bg-brand-bone">
        <div className="container-x py-16 lg:py-24 max-w-3xl">
          <div className="eyebrow mb-3">Common questions</div>
          <h2 className="h2">Holiday lighting FAQs</h2>
          <div className="mt-10 border-t border-brand-border" data-testid="faq-seasonal-lighting">
            {FAQ.map((f, i) => (
              <div key={i} className="py-6 border-b border-brand-border">
                <h3 className="font-serif text-xl text-brand-charcoal">{f.q}</h3>
                <p className="mt-2 font-sans text-base leading-relaxed text-brand-muted">{f.a}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 font-sans text-sm text-brand-muted">
            Looking for year-round details? See the full{' '}
            <Link to="/services/permanent-lighting" className="text-brand-maroon underline underline-offset-4" data-testid="seasonal-permanent-lighting-link">
              permanent lighting service page
            </Link>
            .
          </p>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
